// apps/prompts/src/components/admin/AdminLayout.tsx
import { useAuthStore } from '@neuroforge/shared/client'
import {
  cn,
  LoadingSpinner,
  AppIcon,
  IconChartBar,
  IconUser,
  IconBolt,
  IconMessageCircle,
  IconInbox,
  IconTools,
  IconArrowLeft,
  IconBug,
} from '@neuroforge/ui'
import { Suspense } from 'react'
import { Outlet, NavLink, Link } from 'react-router'

import { useAdminFeedbackNewCount } from '../../hooks/useFeedback'

const ADMIN_NAV = [
  { to: '/admin', label: 'Dashboard', icon: IconChartBar, end: true },
  { to: '/admin/users', label: 'Utilisateurs', icon: IconUser, end: false },
  { to: '/admin/prompts', label: 'Prompts', icon: IconBolt, end: false },
  { to: '/admin/submissions', label: 'Soumissions', icon: IconInbox, end: false },
  { to: '/admin/comments', label: 'Commentaires', icon: IconMessageCircle, end: false },
  { to: '/admin/feedback', label: 'Feedbacks', icon: IconBug, end: false },
]

export default function AdminLayout() {
  const { user } = useAuthStore()
  const { data: feedbackNewCount = 0 } = useAdminFeedbackNewCount()

  return (
    <div className="text-(--text) min-h-screen bg-[var(--bg)]">
      {/* Top bar */}
      <header className="border-(--border) flex items-center justify-between border-b bg-[var(--bg-soft)] px-6 py-4">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="text-(--muted) hover:text-(--text) inline-flex items-center gap-1 text-sm font-semibold transition-colors"
          >
            <AppIcon icon={IconArrowLeft} size={16} aria-hidden /> Retour app
          </Link>
          <div className="h-5 w-px bg-[var(--border)]" />
          <h1 className="flex items-center gap-2 text-lg font-extrabold tracking-tight">
            <AppIcon icon={IconTools} size={20} aria-hidden /> Admin NeuroForge
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-(--muted) text-xs">{user?.email}</span>
          <span className="rounded-lg bg-red-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-red-500">
            Admin
          </span>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar nav */}
        <nav className="border-(--border) min-h-[calc(100vh-57px)] w-56 shrink-0 space-y-1 border-r p-4">
          {ADMIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all',
                  isActive
                    ? 'bg-indigo-500/10 text-indigo-500'
                    : 'text-(--text-secondary) hover:bg-(--bg-elevated)',
                )
              }
            >
              <AppIcon icon={item.icon} size={20} aria-hidden />
              {item.label}
              {item.to === '/admin/feedback' && feedbackNewCount > 0 && (
                <span className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">
                  {feedbackNewCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <Suspense
            fallback={
              <div className="flex justify-center py-20">
                <LoadingSpinner size="lg" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  )
}
